import AddIcon from "@mui/icons-material/Add";
import { Box, Fab } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getArts } from "../../../../actions/arts";
import {
  createDatacenterPrestador,
  getDatacenterPrestadores,
  updateDatacenterPrestador,
} from "../../../../actions/providers";
import snackbarUtils from "../../../../utils/snackbarUtils";
import EditDatacenterPrestadorDialog from "../../../commons/dialog/editDatacenterPrestadorDialog";
import DatacenterPrestadoresTable from "../../../commons/table/datacenterPrestadoresTable";
import DatacenterPrestadoresSearch from "./datacenterPrestadoresSearch";

const initialFilters = { idArt: "", cuit: "", razonSocial: "" };

const initialPrestador = {
  idPrestador: null,
  idArt: "",
  artNombre: "",
  cuit: "",
  razonSocial: "",
  nombreFantasia: "",
  mail: "",
  calle: "",
  numero: "",
  codigoPostal: "",
  provincia: "",
  telefono: "",
  fax: "",
  codigoPrestadorArt: "",
};

const DatacenterPrestadoresContainer = () => {
  const dispatch = useDispatch();
  const arts = useSelector((state) => state.arts.list || []);
  const datacenterPrestadores = useSelector((state) => state.providers.datacenterList);

  const [filters, setFilters] = useState(initialFilters);
  const [lastSearchFilters, setLastSearchFilters] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [prestador, setPrestador] = useState(initialPrestador);
  const [addingNewProvider, setAddingNewProvider] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(getArts());
  }, [dispatch]);

  const artOptions = useMemo(() => [{ id: "", nombre: "Todas" }, ...arts], [arts]);

  const handleSearch = async (values) => {
    const nextFilters = {
      idArt: values.idArt || "",
      cuit: values.cuit?.trim() || "",
      razonSocial: values.razonSocial?.trim() || "",
    };

    setFilters(nextFilters);
    setLastSearchFilters(nextFilters);
    await dispatch(getDatacenterPrestadores(nextFilters));
  };

  const handleReset = () => {
    setFilters(initialFilters);
  };

  const handleOpenNew = () => {
    setPrestador({ ...initialPrestador, idArt: filters.idArt || "" });
    setAddingNewProvider(true);
    setOpenEdit(true);
  };

  const handleOpenEdit = (row) => {
    setPrestador({
      idPrestador: row.idPrestador,
      idArt: row.idArt || "",
      artNombre: row.artNombre || "",
      cuit: row.cuit || "",
      razonSocial: row.razonSocial || "",
      nombreFantasia: row.nombreFantasia || "",
      mail: row.mail || "",
      calle: row.calle || "",
      numero: row.numero || "",
      codigoPostal: row.codigoPostal || "",
      provincia: row.provincia || "",
      telefono: row.telefono || "",
      fax: row.fax || "",
      codigoPrestadorArt: row.codigoPrestadorArt || "",
    });
    setAddingNewProvider(false);
    setOpenEdit(true);
  };

  const refresh = async (nextFilters = lastSearchFilters || filters) => {
    if (!Boolean(nextFilters.idArt || nextFilters.cuit.trim() || nextFilters.razonSocial.trim())) {
      return;
    }

    await dispatch(getDatacenterPrestadores(nextFilters));
  };

  const handleSubmit = async (values, isNew) => {
    const payload = {
      idArt: Number(values.idArt),
      cuit: values.cuit,
      razonSocial: values.razonSocial,
      nombreFantasia: values.nombreFantasia,
      mail: values.mail,
      calle: values.calle,
      numero: values.numero ? Number(values.numero) : null,
      codigoPostal: values.codigoPostal,
      provincia: values.provincia,
      telefono: values.telefono,
      fax: values.fax,
      codigoPrestadorArt: values.codigoPrestadorArt ? Number(values.codigoPrestadorArt) : null,
    };

    setIsSubmitting(true);
    try {
      if (isNew) {
        await dispatch(createDatacenterPrestador(payload));
      } else {
        await dispatch(updateDatacenterPrestador(prestador.idPrestador, payload));
      }

      setOpenEdit(false);
      setPrestador(initialPrestador);
      await refresh();
      snackbarUtils.success(isNew ? "Prestador creado correctamente." : "Prestador actualizado correctamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <DatacenterPrestadoresSearch
        initialValues={filters}
        artOptions={artOptions}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      <DatacenterPrestadoresTable
        data={datacenterPrestadores.list}
        loading={datacenterPrestadores.isFetching}
        fetchData={() => refresh()}
        handleOpenEdit={handleOpenEdit}
      />

      <Fab
        color="primary"
        aria-label="add"
        onClick={handleOpenNew}
        sx={{ position: "fixed", right: 24, bottom: 24, zIndex: 1 }}
      >
        <AddIcon />
      </Fab>

      <EditDatacenterPrestadorDialog
        prestador={prestador}
        arts={arts}
        open={openEdit}
        handleClose={() => setOpenEdit(false)}
        addingNewProvider={addingNewProvider}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
      />
    </Box>
  );
};

export default DatacenterPrestadoresContainer;